import { HubConnectionState } from '@microsoft/signalr';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { ContentId } from 'src/utils/constants/contentId';
import {
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';

export type BulkUpdateInternalNotesDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  onOkay: (
    isUpdate: boolean | null,
    searchString: string | null,
    replaceString: string | null,
    appendString: string | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

export function BulkUpdateInternalNotesDialog({
  updateKey,
  onOkay,
  onCancel,
  entityType,
  ...rest
}: BulkUpdateInternalNotesDialogProps) {
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { refreshData } = usePurchaseDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const [isLoading, setIsLoading] = useState(false);
  const [updateMode, setUpdateMode] = useState<
    'searchAndReplace' | 'clearAndOverwrite' | 'append'
  >('searchAndReplace');
  const [searchString, setSearchString] = useState('');
  const [replaceString, setReplaceString] = useState('');

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        // Depending whether it's Listing, Sales, or Purchase
        // the context would be different here
        if (refreshData) {
          await refreshData(true);
        }
        if (refreshExpandedListItems) {
          await refreshExpandedListItems();
        }
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }

        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshData,
      refreshExpandedListItems,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    entityType,
    BulkActionType.UpdateInternalNotes,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const hasAppendPrivateNoteFeature = true;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const isInputValid = useMemo(() => {
    if (updateMode === 'searchAndReplace') {
      return searchString.length > 0 && replaceString.length > 0;
    }

    return replaceString.length > 0;
  }, [updateMode, replaceString.length, searchString.length]);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      if (onPreviewReceived) {
        onOkay(null, null, null, null, false, onPreviewReceived);
      } else {
        onOkay(
          /* isUpdate */ updateMode === 'clearAndOverwrite',
          searchString,
          /* replaceString */ updateMode === 'append' ? '' : replaceString,
          /* appendString */ updateMode === 'append' ? replaceString : '',
          hasBackgroundBulkEditFeature,
          undefined,
          preview
        );
      }
      setIsLoading(false);
    },
    [
      onOkay,
      updateMode,
      searchString,
      replaceString,
      hasBackgroundBulkEditFeature,
      preview,
    ]
  );

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <GenericDialog
      {...rest}
      size={stage === BulkEditStage.Preview ? 'xl' : 'md'}
      header={
        <BulkEditHeader
          headerText={<Content id={ContentId.UpdatePrivateNotes} />}
        />
      }
      onOpened={() => {
        setUpdateMode('searchAndReplace');
        setSearchString('');
        setReplaceString('');
        initJob();
      }}
      onKeyUp={(e) => {
        if (submittButtonRef.current && e.key === 'Enter' && isInputValid) {
          submittButtonRef.current.click();
        }
      }}
      onClick={(e) => {
        // This so that this dialog does not cause event to flow to the EventHeader open/close event
        e.stopPropagation();
        e.preventDefault();
      }}
      onClosed={() => {
        setMainDialogOpened(false);
        if (progress) {
          setSelectionMode(undefined);
        }
        // Call the outside one if there is one
        rest.onClosed?.();
      }}
      footer={
        <BulkEditFooter
          entityType={entityType}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          disabled={!isInputValid}
          submittButtonRef={submittButtonRef}
        />
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <BulkEditStatus
        entityType={entityType}
        isLoading={isRefreshing || isLoading}
        updateKey={updateKey}
      >
        <Stack direction="column" width="full" gap="l">
          <PosFormField label={<Content id={ContentId.UpdateMode} />}>
            <RadioGroup
              onValueChange={(value: typeof updateMode) => {
                setUpdateMode(value);
              }}
              value={updateMode}
            >
              <Stack direction="column" gap="m">
                <Radio
                  value="searchAndReplace"
                  label={<Content id={ContentId.SearchAndReplace} />}
                />
                <Radio
                  value="clearAndOverwrite"
                  label={<Content id={ContentId.ClearAndOverwrite} />}
                />
                {hasAppendPrivateNoteFeature && (
                  <Radio
                    value="append"
                    label={<Content id={ContentId.Append} />}
                  />
                )}
              </Stack>
            </RadioGroup>
          </PosFormField>

          {updateMode === 'searchAndReplace' && (
            <PosFormField label={<Content id={ContentId.SearchFor} />}>
              <PosTextField
                value={searchString}
                onChange={(v) => setSearchString(v.target.value)}
              />
            </PosFormField>
          )}

          <PosFormField
            label={
              <Content
                id={
                  updateMode === 'clearAndOverwrite'
                    ? ContentId.PrivateNote
                    : updateMode === 'append'
                      ? ContentId.Text
                      : ContentId.ReplaceWith
                }
              />
            }
          >
            <PosTextField
              value={replaceString}
              onChange={(v) => setReplaceString(v.target.value)}
            />
          </PosFormField>
        </Stack>
      </BulkEditStatus>
    </GenericDialog>
  );
}
